import type { CSSProperties } from 'react';

import { cn } from '~/utils/cn';

import { Text } from '../Text/view';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

interface PropsAvatar {
  initials?: string;
  name?: string;
  seed?: string;
  size?: AvatarSize;
  imageUrl?: string;
  className?: string;
  style?: CSSProperties;
}

const PALETTE = [
  '#1D4733',
  '#3F7D5F',
  '#7A4F1B',
  '#0F4C5C',
  '#5C2C44',
  '#2A2D43',
];

const SIZE_MAP: Record<
  AvatarSize,
  { box: number; radius: number; textClass: string }
> = {
  sm: { box: 32, radius: 10, textClass: 'text-captionLG' },
  md: { box: 40, radius: 12, textClass: 'text-paragraphMD' },
  lg: { box: 56, radius: 16, textClass: 'text-paragraphLG' },
  xl: { box: 72, radius: 20, textClass: 'text-headingH2' },
};

const computeInitials = (name?: string, fallback?: string): string => {
  if (fallback) return fallback.slice(0, 2).toUpperCase();
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const colorFor = (seed?: string) => {
  if (!seed) return PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 2147483647;
  }
  const index = Math.abs(hash) % PALETTE.length;
  return PALETTE[index];
};

export default function Avatar({
  initials,
  name,
  seed,
  size = 'md',
  imageUrl,
  className,
  style,
}: PropsAvatar) {
  const sizing = SIZE_MAP[size];
  const bg = colorFor(seed ?? initials ?? name);
  const text = computeInitials(name, initials);

  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden shrink-0',
        className,
      )}
      style={{
        width: sizing.box,
        height: sizing.box,
        borderRadius: sizing.radius,
        backgroundColor: imageUrl ? undefined : bg,
        ...style,
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name ?? 'Avatar'}
          className="w-full h-full object-cover"
        />
      ) : (
        <Text
          className={cn(sizing.textClass, 'color-intense-01 tracking-wide')}
        >
          {text}
        </Text>
      )}
    </div>
  );
}
